import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import Modal from 'components/Modal';
import styled from 'styled-components';
import { useTeamColors } from 'hooks';

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 14px 12px 12px 12px;
  color: #ffffff;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const InfoSection = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  padding: 16px;
`;

const SectionTitle = styled.h3`
  color: ${(props) => props.$color || '#ff1f8c'};
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  flex-shrink: 0;
  margin-right: 12px;
`;

const InfoValue = styled.span`
  color: #ffffff;
  font-size: 14px;
  text-align: right;
  font-weight: 500;
`;

const LocationText = styled.div`
  color: #ffffff;
  font-size: 14px;
  line-height: 1.6;
`;

const AdditionalInfoModal = ({
  show,
  onClose,
  date,
  stadium,
  city,
  region,
  timeZone,
  team,
  opponent,
  channel,
  headline,
  eventState = '3',
  eventType,
}) => {
  const { altColor } = useTeamColors();
  // Get user's timezone
  const userTimezone = moment.tz.guess();

  // Use a fallback timezone if not provided
  const eventTimezone = timeZone || userTimezone;

  // Format dates in both timezones
  const gameTimeLocal = moment.utc(date).tz(eventTimezone);
  const gameTimeUser = moment.utc(date).tz(userTimezone);

  // Check if timezones are different
  const isDifferentTimezone = timeZone && timeZone !== userTimezone;

  // Determine matchup text - eventType '5' (a special/non-matchup event,
  // e.g. an October preseason exhibition like "Big Blue Madness") has no
  // real head-to-head matchup, matching Teams/index.jsx's own suppression
  // of the team/VS-AT/opponent line on the event card for this same type.
  const hasTeamInfo = (team || opponent) && eventType !== '5';
  const matchupText =
    team && opponent
      ? `${team} | ${opponent}`
      : team || opponent || 'Event Details';

  return (
    <Modal onClose={onClose} show={show}>
      <InfoContainer>
        {/* Event Info Section */}
        <InfoSection>
          <SectionTitle $color={altColor}>Event</SectionTitle>
          <InfoRow>
            <InfoLabel>Local Time:</InfoLabel>
            <InfoValue>
              {eventState === '1' ? (
                'TBD'
              ) : (
                <>
                  {gameTimeLocal.format('ddd, MMM D, YYYY')}
                  <br />
                  {eventState === '2'
                    ? 'TBD'
                    : gameTimeLocal.format('h:mm A z')}
                </>
              )}
            </InfoValue>
          </InfoRow>
          {isDifferentTimezone && eventState !== '1' ? (
            <InfoRow>
              <InfoLabel>Your Time:</InfoLabel>
              <InfoValue>
                {gameTimeUser.format('ddd, MMM D, YYYY')}
                <br />
                {eventState === '2' ? 'TBD' : gameTimeUser.format('h:mm A z')}
              </InfoValue>
            </InfoRow>
          ) : null}
        </InfoSection>

        {/* Location Section */}
        <InfoSection>
          <SectionTitle $color={altColor}>Location</SectionTitle>
          <InfoRow>
            <InfoLabel>Venue:</InfoLabel>
            <InfoValue>{stadium}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>City:</InfoLabel>
            <InfoValue>
              {city}
              {region ? `, ${region}` : ''}
            </InfoValue>
          </InfoRow>
          {timeZone ? (
            <InfoRow>
              <InfoLabel>Timezone:</InfoLabel>
              <InfoValue>{timeZone}</InfoValue>
            </InfoRow>
          ) : null}
        </InfoSection>

        {/* Matchup Section */}
        {hasTeamInfo ? (
          <InfoSection>
            <SectionTitle $color={altColor}>Matchup</SectionTitle>
            <LocationText>
              {matchupText} {headline ? `| ${headline}` : ``}{' '}
              {channel ? `| ${channel}` : ``}
            </LocationText>
          </InfoSection>
        ) : null}
      </InfoContainer>
    </Modal>
  );
};

AdditionalInfoModal.propTypes = {
  channel: PropTypes.string,
  city: PropTypes.string,
  date: PropTypes.string.isRequired,
  eventState: PropTypes.string,
  eventType: PropTypes.string,
  headline: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  opponent: PropTypes.string,
  region: PropTypes.string,
  show: PropTypes.bool.isRequired,
  stadium: PropTypes.string,
  team: PropTypes.string,
  timeZone: PropTypes.string,
};

export default AdditionalInfoModal;
